import { Button, CloseButton, IconButton, SaveButton } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useEntities } from '../../../hooks/useEntities';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../data/providers/popupsProvider';
import { IApiKey, ITeam } from '../../../data/types';
import { updateKey } from '../../../data/writers/apiKeys';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { ControlLabel } from '../../shared/form/ControlLabel';
import { FormContainer } from '../../shared/form/FormContainer';
import { TextField } from '../../shared/form/TextField';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';

interface IProps extends RouteComponentProps {
  apiKey: IApiKey;
}

const TeamListContainer = styled.div`
  border: 1px solid var(--color-separator);
  border-radius: 4px;
  background: var(--color-list-background);
  opacity: 0.85;
  position: relative;
`;

const TeamList = styled.div`
  width: 100%;
`;

const TeamItem = styled.div`
  padding: 12px 16px;
  background: transparent;
  border-bottom: 1px solid var(--color-separator);

  &:last-child {
    border-bottom: none;
  }
`;

const TeamName = styled.span`
  color: var(--color-text-secondary);
  font-size: 14px;
`;

const ScopeMessage = styled.div`
  padding: 12px 16px;
  border: 1px solid var(--color-separator);
  border-radius: 4px;
  background: var(--color-list-background);
  color: var(--color-text-secondary);
  font-size: 14px;
  opacity: 0.85;
`;

const HelperText = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
  margin-top: 4px;
  font-style: italic;
`;

/**
 * @description button that opens the edit key popup
 * @param props
 */
export const EditKeyPopup = withRouter((props: IProps): React.ReactElement => {
  /**
   * @description handles opening the popup
   */
  const openDialog = (): void =>
    openInnerPopup(
      <Popup apiKey={props.apiKey} onClose={closeInnerPopup(props.history)} />,
      props.history,
      'edit'
    );

  return <IconButton icon='edit' onClick={openDialog} title='Edit Key' />;
});

interface IEditKeyPopupProps {
  onClose: () => void;
  apiKey: IApiKey;
}

/**
 * @description allows editing an existing api key
 * @param props
 */
const Popup = (props: IEditKeyPopupProps): React.ReactElement => {
  const [appName, setAppName] = React.useState<string>(props.apiKey.appName);
  const [saving, setSaving] = React.useState<boolean>(false);
  const { teams } = useEntities();

  /**
   * @description handles saving the changes to the api key
   */
  const save = (): void => {
    setSaving(true);
    const update = { appName, id: props.apiKey.id };
    updateKey(update).then((): void => {
      setSaving(false);
      props.onClose();
    });
  };

  const isOrgScoped =
    !props.apiKey.teamIds || props.apiKey.teamIds.length === 0;
  const scopedTeams = isOrgScoped
    ? []
    : teams.filter((team: ITeam) => props.apiKey.teamIds?.includes(team.id));

  return (
    <InnerPopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Edit API Key</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {saving && <LinearLoading />}
          <FormContainer>
            <ControlSpacer>
              <TextField
                name='name'
                value={appName}
                label='Name'
                type='text'
                onChange={setAppName}
              />
            </ControlSpacer>
            <ControlSpacer>
              <ControlLabel>Access Scope</ControlLabel>
              {isOrgScoped ? (
                <ScopeMessage>
                  This key has full organization access.
                </ScopeMessage>
              ) : (
                <TeamListContainer>
                  <TeamList>
                    {scopedTeams.map(
                      (team: ITeam): React.ReactElement => (
                        <TeamItem key={team.id}>
                          <TeamName>{team.name}</TeamName>
                        </TeamItem>
                      )
                    )}
                  </TeamList>
                </TeamListContainer>
              )}
            </ControlSpacer>
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton
              onClick={save}
              type='submit'
              disabled={!appName || appName.length === 0}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};
